import * as React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";

import { Button, Paragraph, SimpleLoader } from "../../../../components/atoms";
import {
  InputPhoneCodeContainer,
  IPhoneNumberObject,
} from "../../../../containers/common/input-phone-code";
import { requestPhoneOtp } from "../../../../services/api/customer";
import { ApiError } from "../../../../services/error-handler";
import { theme } from "../../../../theme";
import { config } from "../../../../config";

const ContinueButton = styled(Button)`
  width: 100%;
  margin-top: 24px;
`;

const ErrorParagraph = Paragraph.extend`
  color: ${theme.color.error};
`;

const LinksParagraph = Paragraph.extend`
  margin-top: 24px;
  text-align: center;

  a {
    color: ${theme.color.ruubyGrey};
  }
`;

const Captcha = styled(ReCAPTCHA)`
  margin-top: 20px;

  div{
    margin-left: auto;
    margin-right: auto;
  }
`;

interface IProps {
  loggedOutRoute: string;
}

interface IState {
  errorMessage?: string;
  fullPhoneNumber?: string;
  isContinueButtonEnabled: boolean;
  isLoading: boolean;
  phoneCodeIndex?: number;
  phoneNumber: string;
  captcha: string;
}

type Props = IProps & RouteComponentProps;

class LoginPhoneRequestOtpComponent extends React.PureComponent<
  Props,
  IState
> {
  public state: IState = {
    errorMessage: undefined,
    fullPhoneNumber: undefined,
    isContinueButtonEnabled: false,
    isLoading: false,
    phoneCodeIndex: undefined,
    phoneNumber: "",
    captcha: "",
  };

  /**
   * Submit request for OTP
   */
  private readonly handleClickContinue = async (): Promise<void> => {
    let otpRequestId;

    try {
      if (this.state.fullPhoneNumber) {
        this.setState({ isLoading: true });

        otpRequestId = await requestPhoneOtp(this.state.fullPhoneNumber, this.state.captcha);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "OTP_SMS_CODE_ALREADY_SENT" && err.data?.requestId) {
          otpRequestId = err.data.requestId;
        }

        this.setState({ errorMessage: err.message, isLoading: false });
      }
    } finally {
      if (otpRequestId) {
        this.props.history.replace(
          `${this.props.loggedOutRoute}/phone/validate/${this.state.phoneCodeIndex}/${this.state.phoneNumber}/${otpRequestId}`,
        );
      }
    }
  };

  private readonly handleChangePhoneNumber = (
    phone: IPhoneNumberObject,
  ): void => {
    this.setState({
      fullPhoneNumber: phone.fullPhoneNumber,
      // tslint:disable-next-line:no-magic-numbers
      isContinueButtonEnabled: phone.fullPhoneNumber.length > 5,
      phoneCodeIndex: phone.codeIndex,
      phoneNumber: phone.phoneNumber,
    });
  };

  private readonly handleChangeCaptcha = (captcha: string): void => this.setState({ captcha });

  /**
   * Render
   */
  public render(): JSX.Element {
    const error =
      typeof this.state.errorMessage !== "undefined" ? (
        <ErrorParagraph>{this.state.errorMessage}</ErrorParagraph>
      ) : undefined;

    return (
      <React.Fragment>
        <Helmet>
          <title>Ruuby - Checkout - Login with Phone</title>
        </Helmet>
        <Paragraph>
          To continue, please tell us your mobile phone number
        </Paragraph>
        <InputPhoneCodeContainer
          onNumberChange={this.handleChangePhoneNumber}
        />
        <Captcha
          sitekey={config.reCaptcha.sitekey}
          onChange={this.handleChangeCaptcha}
        />
        <ContinueButton
          size="small"
          onClick={this.handleClickContinue}
          disabled={!this.state.isContinueButtonEnabled || this.state.isLoading}
        >
          {this.state.isLoading ? <SimpleLoader /> : "Continue"}
        </ContinueButton>
        {error}
        <LinksParagraph>
          <Link to={`${this.props.loggedOutRoute}/email`} replace>
            Log in via email instead
          </Link>
        </LinksParagraph>
      </React.Fragment>
    );
  }
}

export const LoginPhoneRequestOtp = withRouter(LoginPhoneRequestOtpComponent);
